/**
 * Learner screen sharing for Kaltura sessions.
 *
 * Kaltura's agent runtime takes **no inbound video track** — unlike Tavus,
 * there is nothing to publish. What it accepts is a still frame pushed over the
 * session socket (`session.sendScreenShot`), gated by the intellect capability
 * `screen_share_analysis` (resolved per session as
 * `clientConfiguration.isScreenShareEnabled`).
 *
 * So the learner shares in our own UI via `getDisplayMedia`, we render the
 * shared surface in the normal session stage, and this module samples that
 * track to a canvas on an interval and pushes JPEG stills. The avatar's
 * perception is therefore sampled, not continuous.
 */

/** How often a still is pushed while sharing. */
export const SCREEN_FRAME_INTERVAL_MS = 2_000;
/** Vision fallback cadence; native Kaltura stills continue at the faster rate. */
export const SCREEN_ANALYSIS_INTERVAL_MS = 8_000;
/** Long edge of the pushed still. Small enough to stay cheap over the socket. */
export const SCREEN_FRAME_MAX_WIDTH = 1_280;
/** JPEG quality for the pushed still. */
const SCREEN_FRAME_QUALITY = 0.6;

/**
 * Instruction appended to the agent prompt while the capability is on.
 *
 * The brain never reads the pushed stills directly — what it can actually rely
 * on is the `shared_screen` object we inject into its dynamic context, so the
 * prompt has to name that contract explicitly. Describing only "you receive
 * still images" made it answer "you are not sharing your screen" while a live
 * description was sitting in its context.
 */
export function screenShareInstruction(): string {
  return (
    "SHARED SCREEN: the learner can share their screen with you. Your live context contains a " +
    '`shared_screen` object: `{ active, status, description, analyzed_at }`. ' +
    "When `active` is true, `description` is a plain-text description of what is on the learner's screen " +
    "right now — treat it as your own eyes: answer questions about the screen from it, quote what it says, " +
    "and never claim the learner is not sharing or that this feature is unavailable. " +
    "The description is a snapshot a few seconds old, so if something just changed ask the learner to hold " +
    "still on it for a moment and it will refresh. " +
    "Only when `active` is false (or `shared_screen` is absent) you cannot see the screen — then invite the " +
    "learner to use the share-screen button rather than guessing."
  );
}

/** App-generated cue sent with `speak()` when sharing starts and the first description lands. */
export function screenShareStartedCue(description: string): string {
  return (
    "[SCREEN SHARE] The learner just started sharing their screen. You can now see it. " +
    `Current screen: ${description}`
  );
}

/** App-generated cue sent with `speak()` when sharing stops. */
export function screenShareStoppedCue(): string {
  return "[SCREEN SHARE] The learner stopped sharing their screen. You can no longer see it.";
}


/**
 * Sample a video track to a downscaled JPEG frame.
 *
 * Returns null while the track has no painted dimensions yet (the first tick
 * after the picker closes), so callers simply skip that beat.
 */
async function grabFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): Promise<ArrayBuffer | null> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const scale = Math.min(1, SCREEN_FRAME_MAX_WIDTH / w);
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", SCREEN_FRAME_QUALITY),
  );
  if (!blob) return null;
  return await blob.arrayBuffer();
}

export type ScreenFramePusher = {
  /** Stop sampling and release the offscreen video/canvas. */
  stop: () => void;
};

/**
 * Start pushing stills from `stream` until `stop()` is called.
 *
 * Every push is best-effort: `sendScreenShot` throws when the session is not
 * connected or the capability is off, and a screen share must never be able to
 * kill the conversation the learner is in — failures are reported through
 * `onError` and sampling simply continues.
 */
export function startScreenFramePusher(input: {
  stream: MediaStream;
  send: (frame: ArrayBuffer, metadata: { width: number; height: number }) => void | Promise<void>;
  onError?: (error: Error) => void;
  intervalMs?: number;
}): ScreenFramePusher {
  const { stream, send, onError } = input;
  const intervalMs = input.intervalMs ?? SCREEN_FRAME_INTERVAL_MS;

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  void video.play().catch(() => {
    /* an unplayable capture just yields no frames */
  });
  const canvas = document.createElement("canvas");

  let stopped = false;
  let inFlight = false;

  const tick = async () => {
    if (stopped || inFlight) return;
    inFlight = true;
    try {
      const frame = await grabFrame(video, canvas);
      if (!stopped && frame) {
        await send(frame, { width: canvas.width, height: canvas.height });
      }
    } catch (e) {
      onError?.(e as Error);
    } finally {
      inFlight = false;
    }
  };

  const timer = window.setInterval(() => void tick(), intervalMs);
  // Don't make the avatar wait a full interval for its first look.
  window.setTimeout(() => void tick(), 400);

  return {
    stop: () => {
      stopped = true;
      window.clearInterval(timer);
      try {
        video.pause();
      } catch {
        /* ignore */
      }
      video.srcObject = null;
    },
  };
}
